import { cn } from '../utilitarios/cn'
import { Botao } from './botao'
import { Selecao } from './selecao'

export const OPCOES_POR_PAGINA = [25, 50, 100, 200] as const

export interface PropriedadesRodapePaginacao {
  limite: number
  onAlterarLimite: (limite: number) => void
  /** "Mostrando 1 a 50 de 512 registros". Sem `total`, some o "de N". */
  quantidadeNaPagina: number
  indiceInicial: number
  total?: number | null
  temAnterior: boolean
  temProximo: boolean
  onAnterior: () => void
  onProximo: () => void
  /** Número da página atual — só existe em paginação por offset (catálogos). */
  pagina?: number
  className?: string
}

/**
 * Rodapé de listagem: seletor de itens por página + resumo + navegação.
 *
 * Serve os dois modos de paginação do contrato (`01-architecture.md §6`): cursor
 * (leads, agendamentos — sem numeração, porque keyset não sabe "página 7") e
 * offset (catálogos e organização, que mostram o número). A diferença aparece só
 * na presença de `pagina` e de `total`.
 */
export function RodapePaginacao({
  limite,
  onAlterarLimite,
  quantidadeNaPagina,
  indiceInicial,
  total,
  temAnterior,
  temProximo,
  onAnterior,
  onProximo,
  pagina,
  className,
}: PropriedadesRodapePaginacao) {
  const fim = indiceInicial + quantidadeNaPagina - 1
  const resumo =
    quantidadeNaPagina === 0
      ? 'Nenhum registro'
      : `Mostrando ${indiceInicial} a ${fim}${
          typeof total === 'number' ? ` de ${total} registros` : ''
        }`

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-borda bg-superficie-2 px-4 py-3',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-xs text-conteudo-suave">
        <label htmlFor="itens-por-pagina" className="whitespace-nowrap">
          Itens por página
        </label>
        <Selecao
          id="itens-por-pagina"
          value={String(limite)}
          onChange={(evento) => onAlterarLimite(Number(evento.target.value))}
          opcoes={OPCOES_POR_PAGINA.map((valor) => ({ valor: String(valor), rotulo: String(valor) }))}
          className="h-8 w-20 text-xs"
        />
      </div>

      <p className="order-last w-full text-center text-xs text-conteudo-fraco sm:order-none sm:w-auto sm:text-left">
        {resumo}
      </p>

      <div className="flex items-center gap-2">
        <Botao variante="contorno" tamanho="sm" onClick={onAnterior} disabled={!temAnterior}>
          Anterior
        </Botao>
        {typeof pagina === 'number' && (
          <span className="min-w-8 rounded-md bg-marca-500 px-2.5 py-1 text-center text-xs font-semibold text-white tabular-nums">
            {pagina}
          </span>
        )}
        <Botao variante="contorno" tamanho="sm" onClick={onProximo} disabled={!temProximo}>
          Próximo
        </Botao>
      </div>
    </div>
  )
}
